import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDataSources, deleteDataSource } from '../api/lens'
import type { LensDataSource } from '../types'
import { Plus, Database, RefreshCw, Trash2, ChevronRight } from 'lucide-react'
import { Button } from '../components/ui/button'

function ago(iso: string | null): string {
  if (!iso) return 'Never'
  const ms = Date.now() - new Date(iso).getTime()
  const m  = Math.floor(ms / 60000)
  if (m < 1)   return 'Just now'
  if (m < 60)  return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24)  return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function LensDataSources() {
  const navigate = useNavigate()
  const [sources, setSources]   = useState<LensDataSource[]>([])
  const [loading, setLoading]   = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    getDataSources().then(setSources).finally(() => setLoading(false))
  }, [])

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('Delete this data source? Scenarios using it will stop working.')) return
    setDeleting(id)
    try {
      await deleteDataSource(id)
      setSources((prev) => prev.filter((s) => s.id !== id))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Database size={16} className="text-accent" />
        <h1 className="text-xl font-semibold text-ink flex-1">Data Sources</h1>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate('/lens/datasources/new')}>
          New data source
        </Button>
      </div>

      <p className="text-sm text-ink-dim mb-5">
        Data sources join data from multiple cups into a flat dataset ready for ML scenarios.
        Refresh a data source to materialise the latest data from Brainer.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-ink-dim py-8">
          <RefreshCw size={13} className="animate-spin" /> Loading…
        </div>
      ) : sources.length === 0 ? (
        <div className="border border-dashed border-paper-border rounded-lg p-12 text-center">
          <Database size={28} className="text-ink-dim mx-auto mb-3" />
          <p className="text-sm font-medium text-ink-dim mb-1">No data sources yet</p>
          <p className="text-theme-xs text-ink-dim mb-4">
            Create one to join data from multiple cups before running an ML scenario.
          </p>
          <Button variant="primary" size="sm" icon={Plus} onClick={() => navigate('/lens/datasources/new')} className="mx-auto">
            Create data source
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((ds) => (
            <div
              key={ds.id}
              onClick={() => navigate(`/lens/datasources/${ds.id}`)}
              className="border border-paper-border rounded-lg px-4 py-3 cursor-pointer hover:border-ink-dim/30 hover:bg-paper-sunken/60 transition-colors flex items-center gap-4"
            >
              <Database size={15} className="text-accent shrink-0" />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink truncate">{ds.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    ds.output_mode === 'aggregate'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-accent-soft text-accent'
                  }`}>
                    {ds.output_mode}
                  </span>
                  {ds.hops.length > 0 && (
                    <span className="text-[10px] text-ink-dim">
                      {ds.hops.length} hop{ds.hops.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-theme-xs text-ink-dim">
                    Root: <span className="text-ink-dim">{ds.root_cup_title || ds.root_cup_id}</span>
                  </span>
                  {ds.description && (
                    <span className="text-theme-xs text-ink-dim truncate">{ds.description}</span>
                  )}
                </div>
              </div>

              <div className="text-right shrink-0">
                {ds.cached_at ? (
                  <>
                    <p className="text-theme-xs text-emerald-600 font-medium">
                      {ds.n_rows.toLocaleString()} rows
                    </p>
                    <p className="text-[10px] text-ink-dim">{ago(ds.cached_at)}</p>
                  </>
                ) : (
                  <p className="text-theme-xs text-amber-500 font-medium">Not cached</p>
                )}
              </div>

              <button
                onClick={(e) => handleDelete(ds.id, e)}
                disabled={deleting === ds.id}
                className="p-1.5 text-ink-dim hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0"
              >
                <Trash2 size={13} />
              </button>

              <ChevronRight size={13} className="text-ink-dim shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
